<template>
  <div class="list-productions-weekly production production-list-weekly">
    <!-- Error Dialog -->
    <div v-if="showErrorDialog" class="error-overlay">
      <div class="error-dialog">
        <div class="error-icon">⚠️</div>
        <h3>Error de Campaña</h3>
        <p>{{ errorMessage }}</p>
        <button @click="closeErrorDialog" class="error-btn">Cerrar</button>
      </div>
    </div>

    <div class="table-container">
      <div class="mobile-export-actions">
        <button
          @click="exportToCSV"
          class="mobile-export-btn mobile-export-btn--compact"
        >
          📄 CSV
        </button>
        <button
          @click="exportToExcel"
          class="mobile-export-btn mobile-export-btn--compact"
        >
          📊 Excel
        </button>
      </div>
      <div class="desktop-export-actions">
        <button @click="exportToCSV" class="desktop-export-btn">📄 CSV</button>
        <button @click="exportToExcel" class="desktop-export-btn">
          📊 Excel
        </button>
      </div>
      <div class="vuetable-body-wrapper">
        <table class="vuetable">
          <thead>
            <tr>
              <th>Finca</th>
              <th>Zona</th>
              <th>Modo</th>
              <th>Categoría</th>
              <th>Formatos</th>
              <th>Nº Cajas</th>
              <th>KG totales</th>
              <th>Nº Frutas</th>
              <th>Semana</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="productions.length === 0">
              <td colspan="9" style="text-align: center; padding: 20px">
                Aún no hay datos introducidos en la tabla
              </td>
            </tr>
            <tr v-for="(item, index) in productions" :key="index">
              <td>{{ item.farmland }}</td>
              <td>{{ item.zone || "-" }}</td>
              <td>{{ getModeLabel(item.production_mode) }}</td>
              <td>{{ getCategoryLabel(item.category) }}</td>
              <td>{{ item.format }}</td>
              <td>{{ item.units }}</td>
              <td>{{ item.total_kg.toFixed(2) }}</td>
              <td>{{ item.fruit_count || "-" }}</td>
              <td>{{ item.week }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div
      v-if="tablePagination && tablePagination.last_page > 1"
      class="control-pagination"
    >
      <button
        class="pagination-btn"
        :disabled="tablePagination.current_page <= 1"
        @click="goToPage(1)"
      >
        &lt;&lt;
      </button>
      <button
        class="pagination-btn"
        :disabled="tablePagination.current_page <= 1"
        @click="goToPage(tablePagination.current_page - 1)"
      >
        &lt;
      </button>
      <span class="pagination-status">
        Pagina {{ tablePagination.current_page }} de
        {{ tablePagination.last_page }}
      </span>
      <button
        class="pagination-btn"
        :disabled="tablePagination.current_page >= tablePagination.last_page"
        @click="goToPage(tablePagination.current_page + 1)"
      >
        &gt;
      </button>
      <button
        class="pagination-btn"
        :disabled="tablePagination.current_page >= tablePagination.last_page"
        @click="goToPage(tablePagination.last_page)"
      >
        &gt;&gt;
      </button>
    </div>
  </div>
</template>

<script>
import * as XLSX from "xlsx";

export default {
  name: "ListProductionWeekly",
  data() {
    return {
      productions: [],
      perPage: 20,
      exportPageSize: 200,
      tablePagination: null,
      showErrorDialog: false,
      errorMessage: "",
      modeLabels: {},
      categoryLabels: {},
    };
  },
  mounted() {
    this.loadLabels();
    this.fetchWeeklyProductions();
  },
  methods: {
    buildWeeklyEndpoint(page = 1, pageSize = this.perPage) {
      return `${this.$hostname}/production/weekly/?page=${page}&page_size=${pageSize}`;
    },

    buildTablePagination(responseData, pageSize, currentPage) {
      const pagination = responseData.pagination || {};
      const total = pagination.total ?? responseData.count ?? 0;

      return {
        total,
        per_page: pagination.per_page ?? pageSize,
        current_page: pagination.current_page ?? currentPage,
        last_page:
          pagination.last_page ??
          (Math.ceil(total / (pageSize || this.perPage)) || 0),
        next_page_url: pagination.next_page_url ?? responseData.next ?? "",
        prev_page_url: pagination.prev_page_url ?? responseData.previous ?? "",
        from: pagination.from ?? 0,
        to: pagination.to ?? 0,
      };
    },

    async loadLabels() {
      try {
        const [modesRes, catsRes] = await Promise.all([
          this.$http.get(`${this.$hostname}/modes/`),
          this.$http.get(`${this.$hostname}/categories/`),
        ]);
        this.modeLabels = Object.fromEntries(
          modesRes.data.map((m) => [m.code, m.name]),
        );
        this.categoryLabels = Object.fromEntries(
          catsRes.data.map((c) => [c.code, c.name]),
        );
      } catch (e) {
        console.error("Error al cargar etiquetas de modos/categorías:", e);
      }
    },
    getModeLabel(mode) {
      return this.modeLabels[mode] || mode || "-";
    },
    getCategoryLabel(category) {
      return this.categoryLabels[category] || category || "-";
    },

    goToPage(page) {
      const parsedPage = Number(page);
      if (!Number.isFinite(parsedPage)) return;

      const currentPage = Number(this.tablePagination?.current_page || 1);
      const lastPage = Number(this.tablePagination?.last_page || parsedPage);
      let targetPage = Math.max(1, Math.trunc(parsedPage));

      if (Number.isFinite(lastPage) && lastPage > 0) {
        targetPage = Math.min(targetPage, lastPage);
      }

      if (targetPage === currentPage) {
        this.fetchWeeklyProductions(currentPage);
        return;
      }

      this.fetchWeeklyProductions(targetPage);
    },

    async fetchWeeklyProductions(page = 1, pageSize = this.perPage) {
      try {
        const response = await this.$http.get(
          this.buildWeeklyEndpoint(page, pageSize),
        );

        this.productions = response.data.results || [];
        this.tablePagination = this.buildTablePagination(
          response.data,
          pageSize,
          page,
        );
      } catch (error) {
        console.error("Error al cargar producción semanal:", error);

        this.productions = [];
        this.tablePagination = {
          total: 0,
          per_page: pageSize,
          current_page: 1,
          last_page: 0,
          next_page_url: "",
          prev_page_url: "",
          from: 0,
          to: 0,
        };

        // Manejar errores de campaña
        if (error.response) {
          if (error.response.status === 404 || error.response.status === 400) {
            this.errorMessage = error.response.data.error;
            this.showErrorDialog = true;
          }
        }
      }
    },

    closeErrorDialog() {
      this.showErrorDialog = false;
      this.$router.push("/production");
    },

    async fetchAllWeeklyRows() {
      const rows = [];
      let currentPage = 1;
      let hasMorePages = true;

      while (hasMorePages) {
        const response = await this.$http.get(
          this.buildWeeklyEndpoint(currentPage, this.exportPageSize),
        );

        rows.push(...(response.data.results || []));

        const pagination = this.buildTablePagination(
          response.data,
          this.exportPageSize,
          currentPage,
        );

        hasMorePages =
          pagination.last_page !== 0 && currentPage < pagination.last_page;

        if (hasMorePages) {
          currentPage += 1;
        }
      }

      return rows;
    },

    async exportToCSV() {
      let rows;

      try {
        rows = await this.fetchAllWeeklyRows();
      } catch (error) {
        this.errorMessage = "Error al exportar producción semanal";
        this.showErrorDialog = true;
        return;
      }

      // Crear encabezados
      const headers = [
        "Finca",
        "Zona",
        "Modo",
        "Categoría",
        "Formatos",
        "Nº Cajas",
        "KG totales",
        "Nº Frutas",
        "Semana",
      ];

      // Crear filas de datos
      const csvRows = rows.map((item) => [
        item.farmland || "",
        item.zone || "-",
        this.getModeLabel(item.production_mode),
        this.getCategoryLabel(item.category),
        item.format || "",
        item.units || 0,
        item.total_kg?.toFixed(2) || 0,
        item.fruit_count || "-",
        item.week || "",
      ]);

      // Combinar encabezados y filas
      const csvContent = [
        headers.join(","),
        ...csvRows.map((row) => row.map((cell) => `"${cell}"`).join(",")),
      ].join("\n");

      // Crear blob y descargar
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `produccion_semanal_${new Date().getTime()}.csv`,
      );
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },

    async exportToExcel() {
      let rows;

      try {
        rows = await this.fetchAllWeeklyRows();
      } catch (error) {
        this.errorMessage = "Error al exportar producción semanal";
        this.showErrorDialog = true;
        return;
      }

      const headers = [
        "Finca",
        "Zona",
        "Modo",
        "Categoría",
        "Formatos",
        "Nº Cajas",
        "KG totales",
        "Nº Frutas",
        "Semana",
      ];

      const excelRows = rows.map((item) => [
        item.farmland || "",
        item.zone || "-",
        this.getModeLabel(item.production_mode),
        this.getCategoryLabel(item.category),
        item.format || "",
        item.units || 0,
        item.total_kg?.toFixed(2) || 0,
        item.fruit_count || "-",
        item.week || "",
      ]);

      const worksheet = XLSX.utils.aoa_to_sheet([headers, ...excelRows]);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Produccion semanal");
      XLSX.writeFile(
        workbook,
        `produccion_semanal_${new Date().getTime()}.xlsx`,
      );
    },
  },
};
</script>

<style scoped>
/* Error Dialog Styles */
.error-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

.error-dialog {
  background: white;
  border-radius: 12px;
  padding: 30px;
  max-width: 400px;
  width: 90%;
  text-align: center;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
}

.error-icon {
  font-size: 60px;
  margin-bottom: 15px;
}

.error-dialog h3 {
  color: #d32f2f;
  margin-bottom: 15px;
  font-size: 20px;
}

.error-dialog p {
  color: #555;
  margin-bottom: 25px;
  line-height: 1.5;
}

.error-btn {
  background: linear-gradient(135deg, #d32f2f 0%, #f44336 100%);
  color: white;
  border: none;
  padding: 12px 30px;
  border-radius: 6px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.error-btn:hover {
  background: linear-gradient(135deg, #b71c1c 0%, #d32f2f 100%);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(211, 47, 47, 0.4);
}

.table-container {
  position: relative;
}

.production-list-weekly {
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.production-list-weekly .table-container {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
}

.production-list-weekly .table-container > .vuetable-body-wrapper {
  flex: 1 1 auto;
  min-height: 0;
  height: auto !important;
}

.control-pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 0 16px;
  flex-shrink: 0;
  padding-bottom: calc(16px + env(safe-area-inset-bottom, 0px));
}

.pagination-btn {
  min-width: 34px;
  height: 32px;
  border-radius: 6px;
  border: 1px solid #d0d5dd;
  background: #ffffff;
  color: #344054;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
}

.pagination-btn:hover:not(:disabled) {
  border-color: #107631;
  color: #107631;
}

.pagination-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.pagination-status {
  min-width: 150px;
  text-align: center;
  font-size: 13px;
  font-weight: 600;
  color: #475467;
}

/* Ocultar botón móvil en escritorio */
.mobile-export-btn {
  display: none !important;
}

.desktop-export-actions {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 6px;
  margin: 0 16px 10px 16px;
}

.mobile-export-actions {
  display: none;
}

/* Estilos específicos para botón de escritorio (blanco) */
.desktop-export-btn {
  padding: 6px 12px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 11px;
  font-weight: 500;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background-color: #fafafa !important;
  color: #666 !important;
  border: 1px solid #ddd !important;
  box-shadow: none !important;
}

.desktop-export-btn:hover {
  background-color: #f5f5f5 !important;
  border-color: #999 !important;
  color: #333 !important;
  box-shadow: none !important;
  transform: none !important;
}

/* Media query para móvil */
@media (max-width: 768px) {
  .mobile-export-actions {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 10px;
    margin: 0 auto 15px auto;
  }

  .mobile-export-btn {
    display: inline-flex !important;
    justify-content: center;
    margin: 0;
    text-align: center;
    width: auto;
    min-width: 140px;
    max-width: 170px;
    padding: 10px 20px;
    font-size: 13px;
    font-weight: 600;
    border-radius: 8px;
    background: linear-gradient(135deg, #107631 0%, #98cf9c 100%);
    color: white;
    border: none;
    box-shadow: 0 4px 6px rgba(16, 118, 49, 0.3);
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .mobile-export-btn--compact {
    min-width: 138px;
    max-width: 160px;
    padding: 10px 14px;
  }

  .mobile-export-btn:hover {
    background: linear-gradient(135deg, #0d5e28 0%, #7ab87d 100%);
    box-shadow: 0 6px 8px rgba(16, 118, 49, 0.4);
    transform: translateY(-1px);
  }

  .desktop-export-actions {
    display: none !important;
  }

  .table-container {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    width: 100%;
  }

  .control-pagination {
    flex-wrap: wrap;
    padding: 10px 0 14px;
  }

  .pagination-status {
    width: 100%;
    order: -1;
    margin-bottom: 6px;
  }

  .vuetable-body-wrapper {
    width: 100%;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }

  .vuetable {
    font-size: 12px;
    min-width: 600px;
  }

  .vuetable thead th {
    font-size: 11px;
    padding: 8px 6px;
    white-space: nowrap;
  }

  .vuetable tbody td {
    font-size: 11px;
    padding: 8px 6px;
  }
}
</style>
